/**
 * Utility functions for Zus token refresh management
 */

const MINIMUM_DELAY_MS = 5000
/**
 * Calculate the delay before the next token refresh attempt.
 * Ensures a minimum delay to prevent rapid-fire requests.
 *
 * @param tokenExpirationTimestamp - Unix timestamp (seconds) when token expires
 * @param bufferSeconds - Seconds before expiry to trigger refresh (default: 60)
 * @returns Delay in milliseconds until next refresh
 */
export const calculateNextRefreshDelay = (
  tokenExpirationTimestamp: number,
  bufferSeconds: number = 60,
): number => {
  const nowInSeconds = Math.floor(Date.now() / 1000)
  const remainingTimeInSeconds = tokenExpirationTimestamp - nowInSeconds
  const delayMs = (remainingTimeInSeconds - bufferSeconds) * 1000

  // Ensure we never schedule a refresh in the past or too soon
  return Math.max(delayMs, MINIMUM_DELAY_MS)
}

/**
 * Determine if a token should be refreshed based on its expiration time.
 *
 * @param tokenExpirationTimestamp - Unix timestamp (seconds) when token expires
 * @param bufferSeconds - Seconds before expiry to consider token "about to expire" (default: 60)
 * @returns true if token should be refreshed, false otherwise
 */
export const shouldRefreshToken = (
  tokenExpirationTimestamp: number,
  bufferSeconds: number = 60,
): boolean => {
  const nowInSeconds = Math.floor(Date.now() / 1000)
  const remainingTimeInSeconds = tokenExpirationTimestamp - nowInSeconds
  return remainingTimeInSeconds <= bufferSeconds
}
